import React, { useCallback } from 'react';

import { TrendingDown, TrendingUp } from '@mui/icons-material';

import { getSeverity, isProtectedData } from 'pages/item/v2/components/ProtectedData';
import ScorePane from 'pages/item/v2/components/Score/components/ScorePane';
import TrendTooltip from 'pages/item/v2/components/Score/components/TrendTooltip';
import { HistoryOfChangesItem } from 'pages/item/v2/types/history-of-changes';

import useStyles from './styles';

interface Props {
    aiScore: {
        value: number;
        uncertanity: number;
    };
}

const AI: React.FC<Props> = ({ aiScore }) => {
    const { classes, cx } = useStyles();
    const { value, uncertanity } = aiScore;

    const getChart = useCallback((data: HistoryOfChangesItem[]) => {
        if (data.every(({ changes }) => 'score' in changes === false)) return null;

        return data.reduce((output, { version, published, changes }) => {
            const score = changes.score?.value ?? output.at(-1)?.score ?? 0;
            output.push({ version, score, published });
            return output;
        }, [] as { score: number; version: number; published: Date }[]);
    }, []);

    if (!value || !uncertanity) {
        return null;
    }

    const isHighConfidence = uncertanity <= 1.3;

    return (
        <TrendTooltip getChart={getChart} title="AI score statistics">
            <ScorePane
                score={value}
                name="AI Score"
                defaultIsOpen
                severity={getSeverity(value)}
                vectorItems={{
                    confidence: isProtectedData(uncertanity) ? (
                        uncertanity
                    ) : (
                        <span
                            className={cx(classes.confidence, {
                                [classes.high]: isHighConfidence,
                                [classes.low]: !isHighConfidence
                            })}
                        >
                            {isHighConfidence ? <TrendingUp /> : <TrendingDown />}
                            {isHighConfidence ? 'High' : 'Low'}
                        </span>
                    )
                }}
            />
        </TrendTooltip>
    );
};

export default AI;
